import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Calendar, ExternalLink, Plus, Users } from 'lucide-react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandShortcut,
} from './ui/command';
import { useEvent } from '../contexts/EventContext';
import { useNavSections } from './nav/useNavSections';
import { teamsQueryKey, fetchTeams } from '../hooks/useTeams';
import { PUBLIC_SITE_URL } from '../config/api';

// Palette ⌘K : aller à un écran, une équipe ou un événement, ou lancer une
// création, sans quitter le clavier. Un organisateur qui gère 150 équipes
// cherche un nom plus souvent qu'il ne parcourt une liste.

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { events, selectedEvent, selectedEventId, setSelectedEventId } = useEvent();
  const sections = useNavSections();

  // Les équipes ne sont chargées qu'à l'ouverture, et partagent le cache de
  // l'écran Équipes : ouvrir la palette depuis cet écran ne refait pas l'appel.
  const teamsQuery = useQuery({
    queryKey: teamsQueryKey(selectedEventId),
    queryFn: () => fetchTeams(selectedEventId as string),
    enabled: open && !!selectedEventId,
  });

  const go = (path: string) => {
    onOpenChange(false);
    navigate(path);
  };

  const publicUrl = selectedEvent?.slug ? `${PUBLIC_SITE_URL}/events/${selectedEvent.slug}` : null;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/70 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className="fixed left-1/2 top-[14vh] z-50 w-[calc(100vw-2rem)] max-w-xl -translate-x-1/2 overflow-hidden rounded-lg border border-white/[0.14] bg-popover rim-strong shadow-2xl shadow-black/60 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 motion-reduce:animate-none"
        >
          <DialogPrimitive.Title className="sr-only">{t('commandPalette.title')}</DialogPrimitive.Title>
          <Command loop>
            <CommandInput placeholder={t('commandPalette.placeholder')} />
            <CommandList>
              <CommandEmpty>{t('commandPalette.empty')}</CommandEmpty>

              {sections.map((section) => (
                <CommandGroup key={section.key} heading={section.label ?? selectedEvent?.name}>
                  {section.items.map((item) => (
                    <CommandItem key={item.key} value={`${section.label ?? ''} ${item.text}`} onSelect={() => go(item.path)}>
                      {item.icon}
                      {item.text}
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))}

              {(teamsQuery.data?.length ?? 0) > 0 && (
                <CommandGroup heading={t('nav.teams')}>
                  {teamsQuery.data!.map((team) => (
                    <CommandItem
                      key={team.id}
                      value={`team ${team.team_id} ${team.name} ${team.participantNames}`}
                      onSelect={() => go(`/admin/teams?team=${team.id}`)}
                    >
                      <Users />
                      <span className="flex-1 truncate">{team.name}</span>
                      <CommandShortcut className="tracking-normal tabular-nums">#{team.team_id}</CommandShortcut>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              <CommandGroup heading={t('commandPalette.actions')}>
                {selectedEventId && (
                  <CommandItem value="action create team" onSelect={() => go('/admin/teams?create=true')}>
                    <Plus />{t('commandPalette.createTeam')}
                  </CommandItem>
                )}
                {selectedEventId && (
                  <CommandItem value="action create wod" onSelect={() => go('/admin/wods/new')}>
                    <Plus />{t('commandPalette.createWod')}
                  </CommandItem>
                )}
                <CommandItem value="action create event" onSelect={() => go('/admin/events?create=true')}>
                  <Plus />{t('commandPalette.createEvent')}
                </CommandItem>
                {publicUrl && (
                  <CommandItem
                    value="action public page"
                    onSelect={() => { onOpenChange(false); window.open(publicUrl, '_blank', 'noopener,noreferrer'); }}
                  >
                    <ExternalLink />{t('nav.viewPublicPage')}
                  </CommandItem>
                )}
              </CommandGroup>

              {events.length > 1 && (
                <CommandGroup heading={t('commandPalette.switchEvent')}>
                  {events.filter((e) => e.id !== selectedEventId).map((e) => (
                    <CommandItem
                      key={e.id}
                      value={`event ${e.name}`}
                      onSelect={() => { setSelectedEventId(e.id); go('/admin/overview'); }}
                    >
                      <Calendar />{e.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/** Raccourci global ⌘K / Ctrl+K. */
export function useCommandPaletteShortcut(setOpen: React.Dispatch<React.SetStateAction<boolean>>) {
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setOpen]);
}
